import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom'; 
import { User, BookOpen, Search, Tag, ArrowRight } from 'lucide-react';
import { blogData } from './blogData'; 

const categories = ["All", "Alumni Spotlight", "Student Voices", "Entrepreneurship", "Technology"];

// --- SKELETON COMPONENT ---
const BlogSkeleton = () => (
  <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-slate-100 flex flex-col h-full animate-pulse">
    <div className="h-52 bg-slate-200" />
    <div className="p-6 flex-1 flex flex-col">
      <div className="w-24 h-4 bg-slate-200 rounded-full mb-4" />
      <div className="w-full h-6 bg-slate-200 rounded mb-2" />
      <div className="w-2/3 h-6 bg-slate-200 rounded mb-6" />
      <div className="flex-1 space-y-2 mb-6">
        <div className="w-full h-3 bg-slate-100 rounded" />
        <div className="w-full h-3 bg-slate-100 rounded" />
      </div>
      <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
        <div className="w-10 h-10 rounded-full bg-slate-200" />
        <div className="space-y-1">
          <div className="w-12 h-2 bg-slate-100 rounded" />
          <div className="w-24 h-3 bg-slate-200 rounded" />
        </div>
      </div>
    </div>
  </div>
);

const Blogs = () => {
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 800);
    return () => clearTimeout(timer);
  }, []);

  const filteredBlogs = blogData.filter(blog => {
    const matchesCategory = filter === "All" || blog.category === filter;
    const matchesSearch = blog.title.toLowerCase().includes(search.toLowerCase()) || 
                          blog.author.toLowerCase().includes(search.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-slate-50 pt-24 pb-20">
      <div className="container mx-auto px-6">
        
        {/* Header */}
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-bold uppercase tracking-widest mb-4"
          >
            <BookOpen size={14} />
            EEA Chronicles
          </motion.div>
          <motion.h1 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl font-extrabold text-slate-900 mb-6"
          >
            Insights & Stories
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-slate-600 text-xl"
          >
            Read about the journeys of our distinguished alumni, student experiences, and the latest in electrical engineering.
          </motion.p>
        </div>

        {/* Filter & Search Bar */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-6 mb-12 bg-white p-4 rounded-2xl shadow-sm border border-slate-100">
          <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0 w-full md:w-auto hide-scrollbar">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap ${
                  filter === cat 
                    ? 'bg-blue-600 text-white shadow-md' 
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="relative w-full md:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input 
              type="text" 
              placeholder="Search stories..." 
              className="w-full pl-10 pr-4 py-2 rounded-full border border-slate-200 focus:outline-none focus:border-blue-500 transition-all"
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Blog Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 min-h-[400px]">
          <AnimatePresence mode="popLayout">
            {isLoading ? (
              [...Array(6)].map((_, i) => (
                <motion.div key={`skeleton-${i}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <BlogSkeleton />
                </motion.div>
              ))
            ) : (
              filteredBlogs.map((blog, idx) => (
                <Link to={`/blogs/${blog.slug}`} key={blog.id}>
                  <motion.div 
                    layout
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.3, delay: idx * 0.05 }}
                    className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group border border-slate-100 flex flex-col h-full cursor-pointer"
                  >
                    
                    {/* --- FIXED IMAGE CONTAINER --- */}
                    <div className="h-52 overflow-hidden relative bg-slate-100 flex items-center justify-center p-2">
                      <img 
                        src={blog.image} 
                        alt={blog.title} 
                        loading="lazy"
                        className="max-h-full max-w-full object-contain transition-transform duration-700 group-hover:scale-105" 
                      />
                      <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-md px-3 py-1 rounded-full text-[10px] font-bold text-blue-700 shadow-sm flex items-center gap-1 border border-blue-50 uppercase tracking-tighter">
                        <Tag size={10} /> {blog.category}
                      </div>
                    </div>

                    {/* Content Section */}
                    <div className="p-6 flex-1 flex flex-col">
                      <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors line-clamp-2">
                        {blog.title}
                      </h3>
                      
                      <p className="text-slate-600 text-sm mb-6 line-clamp-3 flex-1">
                        {blog.desc}
                      </p>

                      {/* Author Info */}
                      <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
                        <div className="w-10 h-10 rounded-full overflow-hidden bg-slate-50 border border-slate-200 flex-shrink-0">
                          {blog.authorImage ? (
                            <img src={blog.authorImage} alt={blog.author} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-slate-400">
                              <User size={20} />
                            </div>
                          )}
                        </div>

                        <div className="flex-1 overflow-hidden">
                          <p className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Featured</p>
                          <p className="text-sm font-bold text-slate-800 leading-tight truncate">{blog.author}</p>
                        </div>
                        
                        <div className="text-slate-400">
                           <ArrowRight size={18} className="group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </Link>
              ))
            )}
          </AnimatePresence>
        </div>

        {/* Empty State */}
        {!isLoading && filteredBlogs.length === 0 && (
          <div className="text-center py-20 text-slate-500">
            <Search size={48} className="mx-auto mb-4 opacity-20" />
            <p className="text-lg">No stories found matching your criteria.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Blogs;